// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/ContentCard.tsx
// Version: v1.3.0
// Description: Content Cards - Responsive Grid: Desktop (cardsPerRow), Tablet 2, Mobile 1

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "../hooks/Container.tsx";
import Button from "./ui/Button.tsx";

// ==================== CONFIGURATION ====================
/**
 * Card Border Radius Configuration (Apple-Style)
 * 
 * Tailwind CSS Klassen:
 * - rounded-none    = 0px
 * - rounded-sm      = 0.125rem (2px)
 * - rounded         = 0.25rem (4px)
 * - rounded-md      = 0.375rem (6px)
 * - rounded-lg      = 0.5rem (8px)
 * - rounded-xl      = 0.75rem (12px)
 * - rounded-2xl     = 1rem (16px)
 * - rounded-3xl     = 1.5rem (24px) ← Apple Standard
 * - rounded-full    = 9999px
 */
const CARD_BORDER_RADIUS = "rounded-3xl"; // Apple-Style: 24px Border Radius

// ==================== INTERFACES ====================
interface ContentCardGridProps {
  heading?: {
    title?: string;
    subtitle?: string;
    description?: string;
  };
  showCarousel?: boolean;
  cardsPerRow?: 1 | 2 | 3 | 4 | 5 | 6;
  data: ContentCardData[];
  className?: string;
}

interface ContentCardData {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  buttonLink?: string;
  buttonText?: string;
}

interface ContentCardItemProps {
  title: string;
  description: string;
  image: string;
  category?: string;
  buttonText?: string;
  buttonLink?: string;
  className?: string;
  isExpanded: boolean;
  onExpand: () => void;
  cardWidth?: string;
  cardHeight?: string;
}

// ==================== CONTENT CARD ITEM COMPONENT ====================
const ContentCardItem = ({ 
  title, 
  description, 
  image, 
  category,
  buttonText = "Mehr erfahren",
  buttonLink = "#",
  className = "",
  isExpanded,
  onExpand,
  cardWidth,
  cardHeight
}: ContentCardItemProps) => {
  const navigate = useNavigate();
  const [defaultHeight, setDefaultHeight] = useState('600px');

  useEffect(() => {
    const updateHeight = () => {
      const width = window.innerWidth;
      
      const minHeight = 450;
      const maxHeight = 550;
      const minWidth = 320;
      const maxWidth = 1920;
      
      const heightRange = maxHeight - minHeight;
      const widthRange = maxWidth - minWidth;
      const widthProgress = Math.max(0, Math.min(1, (width - minWidth) / widthRange));
      
      const calculatedHeight = minHeight + (heightRange * widthProgress);
      
      setDefaultHeight(`${Math.round(calculatedHeight)}px`);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const finalHeight = cardHeight || defaultHeight;
  const finalWidth = cardWidth || 'auto';

  const isInternalLink = buttonLink !== '#' && (
    buttonLink.startsWith('/') || 
    buttonLink.startsWith(window.location.origin)
  );

  const handleButtonClick = (e: React.MouseEvent) => {
    // Im expanded State: Navigiere zum Link
    e.stopPropagation();
    
    if (buttonLink !== '#') {
      if (isInternalLink) {
        navigate(buttonLink);
      } else {
        window.open(buttonLink, '_blank');
      }
    }
  };

  return (
    <div 
      className={`relative ${CARD_BORDER_RADIUS} overflow-hidden cursor-pointer transition-all duration-500 ease-in-out text-white ${className}`}
      style={{ 
        height: finalHeight,
        width: finalWidth,
        minWidth: finalWidth
      }}
      onClick={onExpand}
    >
      <div 
        className={`absolute inset-0 bg-cover transition-all duration-700 ease-in-out ${
          isExpanded 
            ? 'blur-lg scale-150' 
            : 'blur-none scale-100'
        }`}
        style={{ 
          backgroundImage: `url(${image})`,
          backgroundPosition: isExpanded ? 'top left' : 'center'
        }}
      />

      <div className={`absolute inset-0 bg-black transition-opacity duration-500 ${
        isExpanded ? 'bg-opacity-30' : 'bg-opacity-25'
      }`} />

      <div className="font-semibold leading-normal relative h-full flex flex-col p-4 sm:p-5 lg:p-6 text-white">
        
        <div className="flex-shrink-0 space-y-2 sm:space-y-2.5 lg:space-y-3 mb-3">
          <div className="inline-block">
            <span className="tracking-wider text-sm md:text-base lg:text-lg text-white">
              {category}
            </span>
          </div>

          {/* Content Area - Title und Description übereinander positioniert */}
          <div className="relative min-h-[80px]">
            {/* Title - verschwindet beim Expand nach oben */}
            <div className={`absolute inset-0 transition-all duration-500 ease-in-out ${
              isExpanded 
                ? 'opacity-0 transform -translate-y-8 pointer-events-none' 
                : 'opacity-100 transform translate-y-0'
            }`}>
              <h3 className="text-xl md:text-2xl lg:text-3xl text-white">
                {title}
              </h3>
            </div>

            {/* Description - fade-in von unten nach oben, fade-out nach unten */}
            <div className={`absolute inset-0 transition-all duration-500 ease-in-out ${
              isExpanded 
                ? 'opacity-100 transform translate-y-0' 
                : 'opacity-0 transform translate-y-8 pointer-events-none'
            }`}>
              <p className="text-white text-base md:text-lg lg:text-xl">
                {description}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-grow" />

        <div className="flex-shrink-0 relative h-[250px] sm:h-[250px] lg:h-[250px]">
          
          {/* Plus Expand Button - ersetzt das alte Plus-Icon */}
          <div className="absolute bottom-0 right-0">
            <Button
              variant="plus_expand"
              size="M"
              isExpanded={isExpanded}
              onClick={isExpanded ? handleButtonClick : (e) => {
                e.stopPropagation();
                onExpand();
              }}
              to={isExpanded && isInternalLink ? buttonLink : undefined}
              href={isExpanded && !isInternalLink && buttonLink !== '#' ? buttonLink : undefined}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTENT CARD GRID COMPONENT ====================
const ContentCard: React.FC<ContentCardGridProps> = ({ 
  heading,
  showCarousel = false,
  cardsPerRow = 3,
  data,
  className = ""
}) => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest("[data-content-card]")) {
        setExpandedCard(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleCardExpand = (cardId: number) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  const getGridColumns = () => {
    // Responsive: Mobile 1, Tablet 2, Desktop = cardsPerRow
    const desktopCols: Record<number, string> = {
      1: "lg:grid-cols-1",
      2: "lg:grid-cols-2",
      3: "lg:grid-cols-3",
      4: "lg:grid-cols-4",
      5: "lg:grid-cols-5",
      6: "lg:grid-cols-6",
    };
    return `grid-cols-1 md:grid-cols-2 ${desktopCols[cardsPerRow] || "lg:grid-cols-3"}`;
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % data.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + data.length) % data.length);
  const goToSlide = (index: number) => setCurrentSlide(index);

  return (
    <section className={`bg-black text-white mb-[150px] md:mb-[150px] lg:mb-[300px] ${className}`}>
      <Container>
        
        {heading && (
          <div className="mb-6 sm:mb-8 lg:mb-12">
            {heading.subtitle && (
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-3 sm:mb-4">
                {heading.subtitle}
              </p>
            )}
            {heading.title && (
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight text-white">
                {heading.title}
              </h2>
            )}
            {heading.description && (
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed text-white">
                {heading.description}
              </p>
            )}
          </div>
        )}
        
        {!showCarousel && (
          <div className={`grid ${getGridColumns()} gap-4 sm:gap-5 lg:gap-6`}>
            {data.map((card) => (
              <div key={card.id} data-content-card>
                <ContentCardItem  
                  title={card.title}
                  description={card.description}
                  image={card.image}
                  category={card.category}
                  buttonText={card.buttonText}
                  buttonLink={card.buttonLink}
                  isExpanded={expandedCard === card.id}  
                  onExpand={() => handleCardExpand(card.id)}  
                />
              </div>
            ))}
          </div>
        )}

        {showCarousel && (
          <div className="relative">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-300 ease-in-out gap-3 sm:gap-4"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {data.map((card) => (
                  <div 
                    key={card.id} 
                    className="flex-none w-full"
                    data-content-card
                  >
                    <ContentCardItem  
                      title={card.title}
                      description={card.description}
                      image={card.image}
                      category={card.category}
                      buttonText={card.buttonText}
                      buttonLink={card.buttonLink}
                      isExpanded={expandedCard === card.id}  
                      onExpand={() => handleCardExpand(card.id)}  
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center mt-4 sm:mt-6 space-x-2">
              {data.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ${
                    index === currentSlide 
                      ? 'bg-blue-400' 
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-70 transition-all"
            >
              ←
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-70 transition-all" 
            >
              →
            </button>
          </div>
        )}
      </Container>
    </section>
  );
};

export default ContentCard;